// @ts-check
/** @typedef {import('../types/api').HeroSlide} HeroSlide */

import { ArrowDown, ArrowUp, Eye, EyeOff, Film, Image, Plus, RefreshCw, Save, Sparkles, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import heroSlidesService from '../services/heroSlidesService';
import { getVideoThumbnailUrl } from '../utils/mediaThumbnail';
import FileUpload from './FileUpload';
import { FocalPointButton, focalToObjectPosition, toFocal } from './ui/FocalPointPicker';
import ImagePreviewButton from './ui/ImagePreviewButton';

// Storefront hero is 16:9 on desktop and 3:4 on mobile. When a slide has no separate
// mobile image the desktop image is cropped to both, so the picker previews both.
const HERO_DESKTOP_PREVIEWS = [{ label: 'Desktop hero 16:9', ratio: '16 / 9' }];
const HERO_BOTH_PREVIEWS = [
  ...HERO_DESKTOP_PREVIEWS,
  { label: 'Mobile hero 3:4 (no mobile image set)', ratio: '3 / 4' },
];
const HERO_MOBILE_PREVIEWS = [
  { label: 'Mobile hero 3:4', ratio: '3 / 4' },
  { label: 'Tall phones 9:16', ratio: '9 / 16' },
];

// Maps a "kind" of pending replacement to its pending_* / live field pairs, in
// matching order, so handlePublishReplacement/handleDiscardReplacement stay generic.
const PENDING_FIELD_MAP = {
  desktop: {
    pending: [
      'pending_media_type',
      'pending_media_url',
      'pending_cloudinary_public_id',
      'pending_resource_type',
      'pending_focal_x',
      'pending_focal_y',
    ],
    live: ['media_type', 'media_url', 'cloudinary_public_id', 'resource_type', 'focal_x', 'focal_y'],
  },
  mobile: {
    pending: ['pending_mobile_media_url', 'pending_mobile_focal_x', 'pending_mobile_focal_y'],
    live: ['mobile_media_url', 'mobile_focal_x', 'mobile_focal_y'],
  },
};

const HeroSlidesManager = () => {
  const toast = useToast();
  /** @type {[HeroSlide[], Function]} */
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [replacingSlideId, setReplacingSlideId] = useState(null);
  const [replacingMobileSlideId, setReplacingMobileSlideId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingCopyId, setGeneratingCopyId] = useState(null);
  const uploadAreaRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  // Scroll upload area into view when triggered by a mobile-specific upload
  useEffect(() => {
    if (showUpload && replacingMobileSlideId && uploadAreaRef.current) {
      uploadAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showUpload, replacingMobileSlideId]);

  const loadSlides = useCallback(async () => {
    try {
      setLoading(true);
      const data = await heroSlidesService.getAll();
      setSlides(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load hero slides');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  const handleUploadComplete = async (files) => {
    for (const file of files) {
      if (!file.uploaded || !file.url) continue;

      if (replacingMobileSlideId) {
        // Same pending-replacement treatment as the desktop image, below.
        try {
          const updated = await heroSlidesService.update(replacingMobileSlideId, {
            pending_mobile_media_url: file.url,
            pending_mobile_focal_x: null,
            pending_mobile_focal_y: null,
          });
          setSlides((prev) => prev.map((s) => (s.id === replacingMobileSlideId ? { ...s, ...updated } : s)));
          toast.success('Mobile replacement uploaded — set a focal point and publish when ready');
        } catch (err) {
          toast.error('Failed to upload mobile replacement');
          console.error(err);
        }
        setReplacingMobileSlideId(null);
        setShowUpload(false);
        return;
      }

      if (replacingSlideId) {
        // Land the new media as a pending replacement — the live slide is untouched
        // until the admin reviews the focal point and clicks Publish (see below).
        try {
          const updated = await heroSlidesService.update(replacingSlideId, {
            pending_media_type: file.resource_type === 'video' ? 'video' : 'image',
            pending_media_url: file.url,
            pending_cloudinary_public_id: file.public_id,
            pending_resource_type: file.resource_type,
            pending_focal_x: null,
            pending_focal_y: null,
          });
          setSlides((prev) => prev.map((s) => (s.id === replacingSlideId ? { ...s, ...updated } : s)));
          toast.success('Replacement uploaded — set a focal point and publish when ready');
        } catch (err) {
          toast.error('Failed to upload replacement');
          console.error(err);
        }
        setReplacingSlideId(null);
        setShowUpload(false);
        return; // Only replace with first file
      }

      // Create new slide — default the CTA to whatever the most recently added slide used,
      // since most slides in a carousel point at the same handful of destinations.
      const lastSlide = slides[slides.length - 1];
      try {
        // Created inactive: a fresh upload hasn't been reviewed for its focal point yet,
        // so it shouldn't reach the live carousel until the admin activates it.
        const newSlide = await heroSlidesService.create({
          media_type: file.resource_type === 'video' ? 'video' : 'image',
          media_url: file.url,
          cloudinary_public_id: file.public_id,
          resource_type: file.resource_type,
          title: '',
          subtitle: '',
          cta_text: lastSlide?.cta_text || 'Explore Collection',
          cta_link: lastSlide?.cta_link || '/ready-to-wear',
          is_active: false,
        });
        setSlides((prev) => [...prev, newSlide]);
        toast.success('Slide added as inactive — set a focal point, then activate it to go live');
      } catch (err) {
        toast.error('Failed to create slide');
        console.error(err);
      }
    }
    setShowUpload(false);
  };

  const handleReorder = async (slideId, direction) => {
    const idx = slides.findIndex((s) => s.id === slideId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= slides.length) return;

    const updated = [...slides];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((s, i) => (s.display_order = i));
    setSlides(updated);

    try {
      await heroSlidesService.reorder(updated.map((s) => ({ id: s.id, display_order: s.display_order })));
    } catch (err) {
      toast.error('Failed to reorder slides');
      console.error(err);
    }
  };

  const handleToggleActive = async (slide) => {
    try {
      await heroSlidesService.update(slide.id, { is_active: !slide.is_active });
      setSlides((prev) => prev.map((s) => (s.id === slide.id ? { ...s, is_active: !s.is_active } : s)));
    } catch (err) {
      toast.error('Failed to toggle slide');
      console.error(err);
    }
  };

  const handleRemoveMobileImage = async (slideId) => {
    try {
      await heroSlidesService.update(slideId, { mobile_media_url: null, mobile_focal_x: null, mobile_focal_y: null });
      setSlides((prev) =>
        prev.map((s) =>
          s.id === slideId ? { ...s, mobile_media_url: null, mobile_focal_x: null, mobile_focal_y: null } : s,
        ),
      );
      toast.success('Mobile image removed');
    } catch (err) {
      toast.error('Failed to remove mobile image');
      console.error(err);
    }
  };

  const handleDelete = async (slideId) => {
    if (!window.confirm('Delete this slide?')) return;
    try {
      await heroSlidesService.delete(slideId);
      setSlides((prev) => prev.filter((s) => s.id !== slideId));
      toast.success('Slide deleted');
    } catch (err) {
      toast.error('Failed to delete slide');
      console.error(err);
    }
  };

  const handleFieldChange = (slideId, field, value) => {
    setSlides((prev) => prev.map((s) => (s.id === slideId ? { ...s, [field]: value } : s)));
  };

  // Persist a focal point; rethrows so the picker stays open on failure.
  const handleSaveFocal = async (slideId, fields) => {
    try {
      const updated = await heroSlidesService.update(slideId, fields);
      setSlides((prev) => prev.map((s) => (s.id === slideId ? { ...s, ...updated } : s)));
      toast.success('Focal point saved');
    } catch (err) {
      toast.error('Failed to save focal point');
      console.error(err);
      throw err;
    }
  };

  // A pending replacement copies its pending_* fields onto the live ones (desktop or
  // mobile) and clears them; discarding just clears them. Either way the request is a
  // single PATCH — no dedicated backend endpoint needed.
  const handlePublishReplacement = async (slide, kind) => {
    const { pending, live } = PENDING_FIELD_MAP[kind];
    const payload = {};
    live.forEach((liveField, i) => {
      payload[liveField] = slide[pending[i]];
    });
    pending.forEach((field) => {
      payload[field] = null;
    });
    try {
      const updated = await heroSlidesService.update(slide.id, payload);
      setSlides((prev) => prev.map((s) => (s.id === slide.id ? { ...s, ...updated } : s)));
      toast.success('Replacement published');
    } catch (err) {
      toast.error('Failed to publish replacement');
      console.error(err);
    }
  };

  const handleDiscardReplacement = async (slide, kind) => {
    const { pending } = PENDING_FIELD_MAP[kind];
    const payload = {};
    pending.forEach((field) => {
      payload[field] = null;
    });
    try {
      const updated = await heroSlidesService.update(slide.id, payload);
      setSlides((prev) => prev.map((s) => (s.id === slide.id ? { ...s, ...updated } : s)));
      toast.success('Replacement discarded');
    } catch (err) {
      toast.error('Failed to discard replacement');
      console.error(err);
    }
  };

  const handleSaveSlide = async (slide) => {
    setSaving(true);
    try {
      await heroSlidesService.update(slide.id, {
        title: slide.title,
        subtitle: slide.subtitle,
        cta_text: slide.cta_text,
        cta_link: slide.cta_link,
      });
      toast.success('Slide updated');
    } catch (err) {
      toast.error('Failed to save slide');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCopy = async (slide) => {
    const imageUrl = getThumbnailUrl(slide);
    if (!imageUrl) return;
    if (
      (slide.title || slide.subtitle) &&
      !window.confirm('This will replace the current title & subtitle. Continue?')
    ) {
      return;
    }

    setGeneratingCopyId(slide.id);
    try {
      const result = await heroSlidesService.generateCopy(imageUrl);
      setSlides((prev) =>
        prev.map((s) => (s.id === slide.id ? { ...s, title: result.title || '', subtitle: result.subtitle || '' } : s)),
      );
    } catch (err) {
      toast.error(err.message || 'Failed to generate copy');
    } finally {
      setGeneratingCopyId(null);
    }
  };

  // Other slides' CTAs, offered as quick-pick chips instead of retyping the same handful of destinations.
  const getRecentCtas = (currentSlideId) => {
    const seen = new Set();
    const ctas = [];
    for (const s of slides) {
      if (s.id === currentSlideId || !s.cta_text || !s.cta_link) continue;
      const key = `${s.cta_text}|${s.cta_link}`;
      if (seen.has(key)) continue;
      seen.add(key);
      ctas.push({ cta_text: s.cta_text, cta_link: s.cta_link });
    }
    return ctas.slice(0, 4);
  };

  const getThumbnailUrl = (slide) => {
    if (!slide.media_url) return null;
    if (slide.media_type === 'video') {
      return getVideoThumbnailUrl(slide.media_url);
    }
    return slide.media_url;
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading hero slides...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Add Slide Button */}
      <div className="flex justify-end">
        {showUpload ? (
          <button
            onClick={() => {
              setShowUpload(false);
              setReplacingSlideId(null);
              setReplacingMobileSlideId(null);
            }}
            className="flex items-center gap-2 border border-input text-foreground/80 px-4 py-2 rounded-lg hover:bg-accent transition"
          >
            <X size={18} />
            Cancel
          </button>
        ) : (
          <button
            onClick={() => {
              setShowUpload(true);
              setReplacingSlideId(null);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition"
          >
            <Plus size={18} />
            Add Slide
          </button>
        )}
      </div>

      {/* Upload Area */}
      {showUpload && (
        <div ref={uploadAreaRef} className="bg-card border border-border rounded-lg p-4">
          {replacingMobileSlideId && (
            <p className="text-sm text-muted-foreground mb-3">
              <RefreshCw size={14} className="inline mr-1" />
              Upload a portrait image for mobile — ideally 9:16 or 3:4 ratio
            </p>
          )}
          {replacingSlideId && !replacingMobileSlideId && (
            <p className="text-sm text-muted-foreground mb-3">
              <RefreshCw size={14} className="inline mr-1" />
              Replacing media for slide — upload a new image or video
            </p>
          )}
          <FileUpload
            uploadType="hero-media"
            accept={replacingMobileSlideId ? 'image/*' : 'image/*,video/*'}
            multiple={!replacingSlideId && !replacingMobileSlideId}
            maxFiles={replacingSlideId || replacingMobileSlideId ? 1 : 5}
            onUploadComplete={handleUploadComplete}
          />
        </div>
      )}

      {/* Slides List */}
      {slides.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No hero slides configured. Add your first slide to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`flex flex-wrap sm:flex-nowrap items-start gap-4 p-4 border border-border rounded-lg transition ${
                slide.is_active ? 'bg-card' : 'bg-muted opacity-60'
              }`}
            >
              {/* Thumbnails: desktop + mobile */}
              <div className="flex-shrink-0 flex gap-2">
                <div className="w-[150px] h-[100px] bg-gray-900 rounded-lg overflow-hidden relative group">
                  {slide.media_type === 'video' ? (
                    <>
                      {getThumbnailUrl(slide) ? (
                        <img
                          src={getThumbnailUrl(slide)}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={slide.media_url}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Film size={24} className="text-white" />
                      </div>
                    </>
                  ) : slide.media_url ? (
                    <>
                      <ImagePreviewButton src={slide.media_url} alt="Slide" />
                      <FocalPointButton
                        src={slide.media_url}
                        value={toFocal(slide.focal_x, slide.focal_y)}
                        previews={slide.mobile_media_url ? HERO_DESKTOP_PREVIEWS : HERO_BOTH_PREVIEWS}
                        title="Set focal point — desktop image"
                        onSave={(f) => handleSaveFocal(slide.id, { focal_x: f.x, focal_y: f.y })}
                      />
                      <img
                        src={slide.media_url}
                        alt="Slide"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: focalToObjectPosition(toFocal(slide.focal_x, slide.focal_y)) }}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={32} className="text-muted-foreground/70" />
                    </div>
                  )}
                  {/* Replace overlay on hover */}
                  <button
                    onClick={() => {
                      setReplacingSlideId(slide.id);
                      setReplacingMobileSlideId(null);
                      setShowUpload(true);
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="Replace media"
                  >
                    <RefreshCw size={20} className="text-white" />
                  </button>
                </div>

                {/* Mobile portrait image */}
                <div className="flex flex-col items-center gap-1">
                  <div className="w-[56px] h-[100px] bg-muted rounded-lg overflow-hidden relative group border border-dashed border-input">
                    {slide.mobile_media_url ? (
                      <>
                        <ImagePreviewButton src={slide.mobile_media_url} alt="Mobile" />
                        <FocalPointButton
                          src={slide.mobile_media_url}
                          value={toFocal(slide.mobile_focal_x, slide.mobile_focal_y)}
                          previews={HERO_MOBILE_PREVIEWS}
                          title="Set focal point — mobile image"
                          onSave={(f) => handleSaveFocal(slide.id, { mobile_focal_x: f.x, mobile_focal_y: f.y })}
                        />
                        <img
                          src={slide.mobile_media_url}
                          alt="Mobile"
                          className="w-full h-full object-cover"
                          style={{
                            objectPosition: focalToObjectPosition(toFocal(slide.mobile_focal_x, slide.mobile_focal_y)),
                          }}
                        />
                        <button
                          onClick={() => {
                            setReplacingMobileSlideId(slide.id);
                            setReplacingSlideId(null);
                            setShowUpload(true);
                          }}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title="Replace mobile image"
                        >
                          <RefreshCw size={14} className="text-white" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setReplacingMobileSlideId(slide.id);
                          setReplacingSlideId(null);
                          setShowUpload(true);
                        }}
                        className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/70 hover:text-foreground hover:bg-accent transition-colors"
                        title="Upload portrait image for mobile"
                      >
                        <Image size={14} />
                        <span className="text-[9px] mt-1 text-center leading-tight">Mobile</span>
                      </button>
                    )}
                  </div>
                  {slide.mobile_media_url && (
                    <button
                      onClick={() => handleRemoveMobileImage(slide.id)}
                      className="text-[10px] text-red-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Remove mobile image"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Pending replacement — uploaded but not yet live; the thumbnails above still
                  show what customers currently see. */}
              {(slide.pending_media_url || slide.pending_mobile_media_url) && (
                <div className="flex-shrink-0 flex flex-col gap-2 p-2 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 rounded-lg">
                  <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                    Pending replacement
                  </span>
                  {slide.pending_media_url && (
                    <div className="flex items-center gap-2">
                      <div className="w-[100px] h-[70px] bg-gray-900 rounded-lg overflow-hidden relative group">
                        <FocalPointButton
                          src={slide.pending_media_url}
                          value={toFocal(slide.pending_focal_x, slide.pending_focal_y)}
                          previews={
                            slide.pending_mobile_media_url || slide.mobile_media_url
                              ? HERO_DESKTOP_PREVIEWS
                              : HERO_BOTH_PREVIEWS
                          }
                          title="Set focal point — pending desktop image"
                          onSave={(f) => handleSaveFocal(slide.id, { pending_focal_x: f.x, pending_focal_y: f.y })}
                        />
                        {slide.pending_media_type === 'video' ? (
                          <video
                            src={slide.pending_media_url}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={slide.pending_media_url}
                            alt="Pending replacement"
                            className="w-full h-full object-cover"
                            style={{
                              objectPosition: focalToObjectPosition(
                                toFocal(slide.pending_focal_x, slide.pending_focal_y),
                              ),
                            }}
                          />
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handlePublishReplacement(slide, 'desktop')}
                          className="text-[11px] px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                        >
                          Publish
                        </button>
                        <button
                          onClick={() => handleDiscardReplacement(slide, 'desktop')}
                          className="text-[11px] px-2 py-1 border border-input rounded-md hover:bg-accent transition"
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  )}
                  {slide.pending_mobile_media_url && (
                    <div className="flex items-center gap-2">
                      <div className="w-[50px] h-[70px] bg-muted rounded-lg overflow-hidden relative group border border-dashed border-input">
                        <FocalPointButton
                          src={slide.pending_mobile_media_url}
                          value={toFocal(slide.pending_mobile_focal_x, slide.pending_mobile_focal_y)}
                          previews={HERO_MOBILE_PREVIEWS}
                          title="Set focal point — pending mobile image"
                          onSave={(f) =>
                            handleSaveFocal(slide.id, { pending_mobile_focal_x: f.x, pending_mobile_focal_y: f.y })
                          }
                        />
                        <img
                          src={slide.pending_mobile_media_url}
                          alt="Pending mobile replacement"
                          className="w-full h-full object-cover"
                          style={{
                            objectPosition: focalToObjectPosition(
                              toFocal(slide.pending_mobile_focal_x, slide.pending_mobile_focal_y),
                            ),
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handlePublishReplacement(slide, 'mobile')}
                          className="text-[11px] px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                        >
                          Publish
                        </button>
                        <button
                          onClick={() => handleDiscardReplacement(slide, 'mobile')}
                          className="text-[11px] px-2 py-1 border border-input rounded-md hover:bg-accent transition"
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Editable Fields */}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      slide.media_type === 'video'
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                    }`}
                  >
                    {slide.media_type === 'video' ? 'Video' : 'Image'}
                  </span>
                  <span className="text-xs text-muted-foreground/70">#{index + 1}</span>
                  {getThumbnailUrl(slide) && (
                    <button
                      onClick={() => handleGenerateCopy(slide)}
                      disabled={generatingCopyId === slide.id}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition disabled:opacity-50"
                      title="Suggest a title & subtitle from this slide's image"
                    >
                      <Sparkles size={12} className={generatingCopyId === slide.id ? 'animate-pulse' : ''} />
                      {generatingCopyId === slide.id ? 'Generating...' : 'Suggest copy'}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={slide.title || ''}
                    onChange={(e) => handleFieldChange(slide.id, 'title', e.target.value)}
                    placeholder="Title"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                  <input
                    type="text"
                    value={slide.subtitle || ''}
                    onChange={(e) => handleFieldChange(slide.id, 'subtitle', e.target.value)}
                    placeholder="Subtitle"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                  <input
                    type="text"
                    value={slide.cta_text || ''}
                    onChange={(e) => handleFieldChange(slide.id, 'cta_text', e.target.value)}
                    placeholder="CTA Text"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                  <input
                    type="text"
                    value={slide.cta_link || ''}
                    onChange={(e) => handleFieldChange(slide.id, 'cta_link', e.target.value)}
                    placeholder="CTA Link"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>

                {getRecentCtas(slide.id).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-xs text-muted-foreground/70">Reuse CTA:</span>
                    {getRecentCtas(slide.id).map((cta) => (
                      <button
                        key={`${cta.cta_text}|${cta.cta_link}`}
                        onClick={() => {
                          handleFieldChange(slide.id, 'cta_text', cta.cta_text);
                          handleFieldChange(slide.id, 'cta_link', cta.cta_link);
                        }}
                        className="text-xs px-2 py-0.5 bg-muted hover:bg-accent rounded-full text-muted-foreground transition"
                        title={cta.cta_link}
                      >
                        {cta.cta_text}
                      </button>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => handleSaveSlide(slide)}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition mt-1"
                >
                  <Save size={14} />
                  Save
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={() => handleReorder(slide.id, 'up')}
                  disabled={index === 0}
                  className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                  title="Move up"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={() => handleReorder(slide.id, 'down')}
                  disabled={index === slides.length - 1}
                  className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                  title="Move down"
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  onClick={() => handleToggleActive(slide)}
                  className="p-1.5 rounded-lg hover:bg-accent transition"
                  title={slide.is_active ? 'Deactivate' : 'Activate'}
                >
                  {slide.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => handleDelete(slide.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroSlidesManager;
